/**
 * HR Engine - Page Détail de Campagne d'Évaluation (Nouveau module Performance)
 * Affiche les détails d'une campagne et la liste des évaluations
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import EditObjectivesDialog from '../components/EditObjectivesDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  RefreshCw,
  Target,
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  Play,
  Eye,
  Download,
  Star,
  AlertTriangle,
  Send,
  Loader2,
  Search,
  Building2,
  TrendingUp,
  Award,
  XCircle,
  ChevronRight,
  ArrowRight,
  FileText,
  Pencil
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: Play },
  self_eval: { label: 'Auto-évaluation', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  manager_eval: { label: 'Évaluation manager', color: 'bg-orange-100 text-orange-700', icon: Target },
  validation: { label: 'Validation', color: 'bg-purple-100 text-purple-700', icon: CheckCircle2 },
  completed: { label: 'Terminée', color: 'bg-blue-100 text-blue-700', icon: Award },
  archived: { label: 'Archivée', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

const EVAL_STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-slate-100 text-slate-700' },
  self_eval_pending: { label: 'Auto-éval. en attente', color: 'bg-yellow-100 text-yellow-700' },
  self_eval_done: { label: 'Auto-éval. terminée', color: 'bg-blue-100 text-blue-700' },
  manager_eval_pending: { label: 'Éval. manager en attente', color: 'bg-orange-100 text-orange-700' },
  manager_eval_done: { label: 'Évalué par manager', color: 'bg-purple-100 text-purple-700' },
  n2_validation_pending: { label: 'Validation N+2', color: 'bg-indigo-100 text-indigo-700' },
  validated: { label: 'Validé', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Complété', color: 'bg-emerald-100 text-emerald-700' }
};

export default function PerformanceCampaignDetailPage() {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [editObjectivesEval, setEditObjectivesEval] = useState(null);
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('evaluations');
  const [launching, setLaunching] = useState(false);
  const [showLaunchDialog, setShowLaunchDialog] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [hierarchyDrift, setHierarchyDrift] = useState({ count: 0, items: [] });
  const [resyncing, setResyncing] = useState(false);
  const [showDriftDetails, setShowDriftDetails] = useState(false);
  const [resyncModalOpen, setResyncModalOpen] = useState(false);
  const [resyncPreview, setResyncPreview] = useState(null);
  const [selectedNewEmployeeIds, setSelectedNewEmployeeIds] = useState(new Set());

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  useEffect(() => {
    filterEvaluations();
  }, [evaluations, searchQuery, statusFilter]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      
      // Fetch campaign details
      const campaignRes = await api.get(`/performance-eval/campaigns/${campaignId}`);
      setCampaign(campaignRes.data);

      // Fetch evaluations for this campaign
      const evalsRes = await api.get(`/performance-eval/campaigns/${campaignId}/evaluations`);
      setEvaluations(evalsRes.data.evaluations || []);

      // Fetch hierarchy drift (evaluations where the stored manager != current manager)
      try {
        const driftRes = await api.get(`/performance-eval/campaigns/${campaignId}/hierarchy-drift`);
        setHierarchyDrift(driftRes.data || { count: 0, items: [] });
      } catch (_e) {
        setHierarchyDrift({ count: 0, items: [] });
      }

    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Erreur lors du chargement de la campagne');
    } finally {
      setLoading(false);
    }
  };

  const handleResyncHierarchy = async () => {
    // Step 1 — fetch preview, open confirmation modal
    try {
      setResyncing(true);
      const r = await api.get(`/performance-eval/campaigns/${campaignId}/resync-preview`);
      setResyncPreview(r.data);
      // Pre-select all new employees by default
      setSelectedNewEmployeeIds(new Set((r.data?.newEmployees || []).map((e) => e.id)));
      setResyncModalOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la préparation');
    } finally {
      setResyncing(false);
    }
  };

  const handleConfirmResync = async () => {
    try {
      setResyncing(true);
      const payload = {
        addEmployeeIds: Array.from(selectedNewEmployeeIds),
        skipNewEmployees: selectedNewEmployeeIds.size === 0,
      };
      const r = await api.post(`/performance-eval/campaigns/${campaignId}/resync-hierarchy`, payload);
      toast.success(r.data?.message || 'Resynchronisation terminée');
      setResyncModalOpen(false);
      setResyncPreview(null);
      await fetchCampaign();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la synchronisation');
    } finally {
      setResyncing(false);
    }
  };

  const filterEvaluations = () => {
    let filtered = [...evaluations];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e => {
        const name = `${e.employeeSnapshot?.firstName || ''} ${e.employeeSnapshot?.lastName || ''}`.toLowerCase();
        const dept = (e.departmentName || '').toLowerCase();
        return name.includes(q) || dept.includes(q);
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    setFilteredEvaluations(filtered);
  };

  const handleLaunch = async () => {
    try {
      setLaunching(true);
      await api.post(`/performance-eval/campaigns/${campaignId}/launch`);
      toast.success('Campagne lancée avec succès !');
      setShowLaunchDialog(false);
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du lancement');
    } finally {
      setLaunching(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      setSendingReminders(true);
      const response = await api.post(`/performance-eval/campaigns/${campaignId}/send-reminders`);
      const data = response.data;
      
      if (data.remindersSent > 0) {
        toast.success(`${data.remindersSent} rappel(s) envoyé(s) avec succès !`);
      } else {
        toast.info(data.message || 'Aucun rappel à envoyer');
      }
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.totalErrors} erreur(s) lors de l'envoi`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi des rappels');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleComplete = async () => {
    try {
      await api.post(`/performance-eval/campaigns/${campaignId}/complete`);
      toast.success('Campagne terminée');
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
    }
  };

  const downloadPDF = async (evaluationId, employeeName) => {
    try {
      toast.info('Génération du PDF en cours...');
      
      const response = await api.get(`/performance-eval/evaluations/${evaluationId}/pdf`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Evaluation_${employeeName || 'Performance'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const downloadCampaignSummaryPDF = async () => {
    try {
      toast.info('Génération de la synthèse PDF en cours...');
      
      const response = await api.get(`/performance-eval/campaigns/${campaignId}/summary-pdf`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Synthese_${campaign?.name || 'Campagne'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Synthèse PDF téléchargée avec succès !');
    } catch (error) {
      console.error('Erreur téléchargement synthèse:', error);
      toast.error('Erreur lors du téléchargement de la synthèse');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Détail Campagne">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout title="Campagne">
        <Card className="py-12">
          <CardContent className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Campagne introuvable</h3>
            <p className="text-slate-500 mb-4">Cette campagne n'existe pas ou a été supprimée.</p>
            <Button onClick={() => navigate('/evaluation-campaigns')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux campagnes
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const stats = campaign.stats || campaign.analytics || {};

  // Calculate progress - use evaluations array if stats not available
  const total = stats.totalParticipants || campaign.evaluations?.length || evaluations.length || 0;
  const selfDone = stats.selfEvalCompleted || evaluations.filter(e => 
    ['self_eval_done', 'manager_eval_pending', 'manager_eval_done', 'n2_validation_pending', 'validated', 'completed'].includes(e.status)
  ).length || 0;
  const managerDone = stats.managerEvalCompleted || evaluations.filter(e => 
    ['manager_eval_done', 'n2_validation_pending', 'validated', 'completed'].includes(e.status)
  ).length || 0;
  const validated = stats.validated || evaluations.filter(e => 
    ['validated', 'completed'].includes(e.status)
  ).length || 0;
  const completed = stats.completed || evaluations.filter(e => e.status === 'completed').length || 0;

  return (
    <DashboardLayout title={campaign.name}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              className="mb-2 -ml-2"
              onClick={() => navigate('/evaluation-campaigns')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux campagnes
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-indigo-500" />
              {campaign.name}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={statusConfig.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
              <span className="text-slate-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(campaign.periodStart).toLocaleDateString('fr-FR')} - {new Date(campaign.periodEnd).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={fetchCampaign}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={downloadCampaignSummaryPDF}>
              <FileText className="w-4 h-4 mr-2" />
              Synthèse PDF
            </Button>
            {campaign.status === 'active' && (
              <Button 
                variant="outline" 
                onClick={handleSendReminders}
                disabled={sendingReminders}
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                data-testid="send-reminders-btn"
              >
                {sendingReminders ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer rappels
              </Button>
            )}
            {/* Resynchroniser — always visible for Admin/SuperAdmin on running campaigns.
                Re-applies the current hierarchy + updated employee data to the campaign.
                Shows a count badge when drift is detected. */}
            {(user?.role === 'Admin' || user?.role === 'SuperAdmin') &&
              ['active', 'self_eval', 'manager_eval', 'validation'].includes(campaign.status) && (
              <Button
                variant="outline"
                onClick={handleResyncHierarchy}
                disabled={resyncing}
                className="text-orange-600 border-orange-200 hover:bg-orange-50 relative"
                data-testid="resync-hierarchy-top-btn"
                title="Resynchroniser les évaluations avec la hiérarchie et les infos employés actuelles"
              >
                {resyncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Resynchroniser
                {hierarchyDrift.count > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-orange-600 text-white text-[10px] font-bold">
                    {hierarchyDrift.count}
                  </span>
                )}
              </Button>
            )}
            {campaign.status === 'draft' && (
              <Button onClick={() => setShowLaunchDialog(true)} className="bg-green-600 hover:bg-green-700">
                <Play className="w-4 h-4 mr-2" />
                Lancer la campagne
              </Button>
            )}
            {['active', 'self_eval', 'manager_eval', 'validation'].includes(campaign.status) && (
              <Button variant="outline" onClick={handleComplete}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Clôturer
              </Button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto text-slate-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{total}</p>
              <p className="text-sm text-slate-500">Participants</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto text-yellow-500 mb-2" />
              <p className="text-2xl font-bold text-yellow-600">{selfDone}</p>
              <p className="text-sm text-slate-500">Auto-éval.</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold text-orange-600">{managerDone}</p>
              <p className="text-sm text-slate-500">Éval. manager</p>
            </CardContent>
          </Card>
          <Card className="border-green-200">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{validated}</p>
              <p className="text-sm text-slate-500">Validés</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-4 text-center">
              <Award className="w-6 h-6 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-blue-600">{completed}</p>
              <p className="text-sm text-slate-500">Complétés</p>
            </CardContent>
          </Card>
        </div>

        {/* Hierarchy drift alert — visible uniquement aux Admin/SuperAdmin */}
        {hierarchyDrift.count > 0 && (user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
          <Card className="border-orange-300 bg-orange-50" data-testid="hierarchy-drift-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-orange-900">
                    {hierarchyDrift.count} évaluation{hierarchyDrift.count > 1 ? 's' : ''} désynchronisée{hierarchyDrift.count > 1 ? 's' : ''} avec la hiérarchie actuelle
                  </h3>
                  <p className="text-sm text-orange-800 mt-1">
                    Des employés ont changé de manager depuis le lancement. Le manager stocké dans l'évaluation ne correspond plus au manager réel.
                    <button
                      type="button"
                      className="ml-2 underline font-medium"
                      onClick={() => setShowDriftDetails(!showDriftDetails)}
                    >
                      {showDriftDetails ? 'Masquer' : 'Voir'} le détail
                    </button>
                  </p>
                  {showDriftDetails && (
                    <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-orange-200 bg-white divide-y divide-orange-100">
                      {hierarchyDrift.items.map((item) => (
                        <div key={item.evaluationId} className="px-3 py-2 text-sm flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-900">{item.employeeName}</span>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-600 line-through">{item.oldManagerName}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                          <span className="font-medium text-emerald-700">{item.newManagerName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleResyncHierarchy}
                  disabled={resyncing}
                  className="bg-orange-600 hover:bg-orange-700 flex-shrink-0"
                  data-testid="resync-hierarchy-btn"
                >
                  {resyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Resynchroniser
                </Button>
              </div>
              <p className="text-xs text-orange-700 mt-2 ml-9">
                ℹ Les notes et commentaires déjà saisis seront conservés — seul le destinataire de l'évaluation est mis à jour.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progression globale</span>
              <span className="text-sm text-slate-500">
                {total > 0 ? Math.round((completed / total) * 100) : 0}%
              </span>
            </div>
            <Progress value={total > 0 ? (completed / total) * 100 : 0} className="h-2" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="evaluations" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Évaluations ({filteredEvaluations.length})
            </TabsTrigger>
            <TabsTrigger value="info" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Informations
            </TabsTrigger>
          </TabsList>

          {/* Evaluations tab */}
          <TabsContent value="evaluations" className="mt-4">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un employé..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(EVAL_STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Evaluations list */}
            {filteredEvaluations.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">
                    {evaluations.length === 0 
                      ? 'Aucune évaluation dans cette campagne' 
                      : 'Aucun résultat pour cette recherche'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredEvaluations.map(evaluation => {
                  const emp = evaluation.employeeSnapshot || {};
                  const evalStatus = EVAL_STATUS_CONFIG[evaluation.status] || EVAL_STATUS_CONFIG.pending;
                  const isCompleted = ['validated', 'completed'].includes(evaluation.status);
                  const canEditObjectives = !['manager_eval_done', 'n2_validated', 'validated', 'completed'].includes(evaluation.status);
                  const score = evaluation.finalScore;

                  return (
                    <Card 
                      key={evaluation.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/performance/evaluate-campaign/${evaluation.employeeId}?evaluation=${evaluation.id}`)}
                    >
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-indigo-100 text-indigo-700">
                                {emp.firstName?.[0]}{emp.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-slate-900">
                                {emp.firstName} {emp.lastName}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <span>{emp.jobTitle || 'Poste non défini'}</span>
                                {evaluation.departmentName && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <Building2 className="w-3 h-3" />
                                      {evaluation.departmentName}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <Badge className={evalStatus.color}>{evalStatus.label}</Badge>
                            
                            {score !== null && score !== undefined && (
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                                score >= 85 ? 'bg-emerald-100 text-emerald-700' :
                                score >= 60 ? 'bg-blue-100 text-blue-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {score}
                              </div>
                            )}

                            {canEditObjectives && (user?.role === 'Admin' || user?.role === 'SuperAdmin' || user?.role === 'Manager') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditObjectivesEval(evaluation);
                                }}
                                data-testid={`edit-objectives-btn-${evaluation.id}`}
                                title="Modifier les objectifs (avant évaluation manager)"
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Objectifs
                              </Button>
                            )}

                            {isCompleted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const empName = `${evaluation.employee?.firstName || ''}_${evaluation.employee?.lastName || ''}`.trim().replace(/\s+/g, '_');
                                  downloadPDF(evaluation.id, empName);
                                }}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                            )}

                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Info tab */}
          <TabsContent value="info" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Période d'évaluation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Début</p>
                      <p className="font-medium">{new Date(campaign.periodStart).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Fin</p>
                      <p className="font-medium">{new Date(campaign.periodEnd).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm text-slate-500 mb-1">Type de campagne</p>
                    <Badge variant="outline">
                      {campaign.campaignType === 'annual' ? 'Évaluation annuelle' :
                       campaign.campaignType === 'midterm' ? 'Évaluation mi-année' :
                       campaign.campaignType === 'probation' ? 'Période d\'essai' : campaign.campaignType}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dates limites</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Auto-évaluation</span>
                    <span className="font-medium">
                      {campaign.selfEvalEndDate && new Date(campaign.selfEvalEndDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Évaluation manager</span>
                    <span className="font-medium">
                      {campaign.managerEvalEndDate && new Date(campaign.managerEvalEndDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Validation</span>
                    <span className="font-medium">
                      {campaign.validationEndDate && new Date(campaign.validationEndDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {campaign.description && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">{campaign.description}</p>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{stats.highPerformers || 0}</p>
                      <p className="text-xs text-slate-500">High Performers</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{stats.performers || 0}</p>
                      <p className="text-xs text-slate-500">Performers</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{stats.nonPerformers || 0}</p>
                      <p className="text-xs text-slate-500">Non Performers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Paramètres</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Validation N+2</span>
                    <Badge variant={campaign.includeN2Validation ? "default" : "outline"}>
                      {campaign.includeN2Validation ? 'Activée' : 'Désactivée'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Notifications email</span>
                    <Badge variant={campaign.sendNotifications ? "default" : "outline"}>
                      {campaign.sendNotifications ? 'Activées' : 'Désactivées'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Launch dialog */}
        <AlertDialog open={showLaunchDialog} onOpenChange={setShowLaunchDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Lancer la campagne</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir lancer la campagne "{campaign.name}" ?
                <br /><br />
                <strong>{total} employé(s)</strong> seront inclus dans cette campagne.
                {campaign.sendNotifications && (
                  <> Des notifications email seront envoyées.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLaunch}
                disabled={launching}
                className="bg-green-600 hover:bg-green-700"
              >
                {launching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Lancer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog : Modifier les objectifs d'une évaluation */}
        <EditObjectivesDialog
          open={!!editObjectivesEval}
          onOpenChange={(o) => !o && setEditObjectivesEval(null)}
          evaluation={editObjectivesEval}
          onSaved={(newObjectives) => {
            setEvaluations(prev => prev.map(ev =>
              ev.id === editObjectivesEval?.id ? { ...ev, objectives: newObjectives } : ev
            ));
          }}
        />

        {/* Dialog : Aperçu de la resynchronisation */}
        <ResyncPreviewDialog
          open={resyncModalOpen}
          onOpenChange={setResyncModalOpen}
          preview={resyncPreview}
          selectedIds={selectedNewEmployeeIds}
          onToggleId={(id) => {
            setSelectedNewEmployeeIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            });
          }}
          onToggleAll={(all) => {
            if (!preview) return;
          }}
          onSelectAll={() => {
            const all = new Set((resyncPreview?.newEmployees || []).map((e) => e.id));
            setSelectedNewEmployeeIds(all);
          }}
          onSelectNone={() => setSelectedNewEmployeeIds(new Set())}
          onConfirm={handleConfirmResync}
          submitting={resyncing}
        />
      </div>
    </DashboardLayout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Resync preview modal
// ──────────────────────────────────────────────────────────────────────

function ResyncPreviewDialog({
  open, onOpenChange, preview, selectedIds, onToggleId,
  onSelectAll, onSelectNone, onConfirm, submitting,
}) {
  if (!preview) return null;
  const t = preview.totals || {};
  const newEmployees = preview.newEmployees || [];
  const managerReassignments = preview.managerReassignments || [];
  const snapshotUpdates = preview.snapshotUpdates || [];
  const objectivesUpdates = preview.objectivesUpdates || [];
  const totalChanges = selectedIds.size + t.managerReassignments + t.snapshotUpdates + t.objectivesUpdates;
  const nothingToDo = totalChanges === 0;

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="resync-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-orange-600" />
            Aperçu de la resynchronisation
          </DialogTitle>
          <DialogDescription>
            Vérifiez les changements avant d'appliquer la resynchronisation. Les notes et commentaires déjà saisis ne seront jamais écrasés.
          </DialogDescription>
        </DialogHeader>

        {nothingToDo ? (
          <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-4 text-sm text-emerald-700" data-testid="resync-nothing">
            ✓ Tout est déjà à jour. Aucun changement n'est nécessaire.
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* New employees */}
            <div className="rounded-lg border border-ink-200">
              <div className="flex items-center justify-between px-4 py-3 border-b bg-orange-50/40">
                <div>
                  <div className="text-sm font-semibold text-ink-900">
                    👤 Nouveaux employés à ajouter <span className="ml-1 text-orange-700">({t.newEmployees})</span>
                  </div>
                  <div className="text-[11px] text-ink-500">
                    Affectés aux départements/sites ciblés mais sans évaluation pour cette campagne.
                  </div>
                </div>
                {newEmployees.length > 0 && (
                  <div className="flex gap-1 text-xs">
                    <button onClick={onSelectAll} className="px-2 py-1 rounded border border-ink-200 hover:bg-ink-50" data-testid="resync-select-all">Tout</button>
                    <button onClick={onSelectNone} className="px-2 py-1 rounded border border-ink-200 hover:bg-ink-50" data-testid="resync-select-none">Aucun</button>
                  </div>
                )}
              </div>
              {newEmployees.length === 0 ? (
                <div className="px-4 py-3 text-xs text-ink-500">Aucun nouvel employé.</div>
              ) : (
                <ul className="divide-y divide-ink-100 max-h-56 overflow-y-auto">
                  {newEmployees.map((e) => (
                    <li key={e.id} className="flex items-center gap-3 px-4 py-2 hover:bg-ink-50">
                      <Checkbox
                        checked={selectedIds.has(e.id)}
                        onCheckedChange={() => onToggleId(e.id)}
                        data-testid={`resync-employee-${e.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink-900 truncate">
                          {e.firstName} {e.lastName}
                          {e.matricule && <span className="ml-2 text-[10px] text-ink-500 font-mono">{e.matricule}</span>}
                        </div>
                        <div className="text-[11px] text-ink-500 truncate">
                          {e.jobTitle || '—'} · {e.departmentName} · {e.siteName}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Manager reassignments */}
            {managerReassignments.length > 0 && (
              <div className="rounded-lg border border-ink-200">
                <div className="px-4 py-3 border-b bg-violet-50/40">
                  <div className="text-sm font-semibold text-ink-900">
                    🔁 Manager à réaffecter <span className="ml-1 text-violet-700">({t.managerReassignments})</span>
                  </div>
                </div>
                <ul className="divide-y divide-ink-100 max-h-40 overflow-y-auto">
                  {managerReassignments.slice(0, 30).map((d) => (
                    <li key={d.evaluationId} className="px-4 py-2 text-xs text-ink-700">
                      {d.employeeName || d.employeeId} <span className="text-ink-400">→</span>{' '}
                      <span className="font-medium">{d.newManagerName || 'Nouveau manager'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Snapshot updates */}
            {snapshotUpdates.length > 0 && (
              <div className="rounded-lg border border-ink-200">
                <div className="px-4 py-3 border-b bg-blue-50/40">
                  <div className="text-sm font-semibold text-ink-900">
                    📝 Fiches employé à mettre à jour <span className="ml-1 text-blue-700">({t.snapshotUpdates})</span>
                  </div>
                </div>
                <ul className="divide-y divide-ink-100 max-h-40 overflow-y-auto">
                  {snapshotUpdates.slice(0, 30).map((s) => (
                    <li key={s.evaluationId} className="px-4 py-2 text-xs text-ink-700">
                      <span className="font-medium">{s.employeeName}</span>
                      <span className="text-ink-400"> · </span>
                      <span className="text-ink-500">champs modifiés : {s.changedFields.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Objectives updates */}
            {objectivesUpdates.length > 0 && (
              <div className="rounded-lg border border-ink-200">
                <div className="px-4 py-3 border-b bg-emerald-50/40">
                  <div className="text-sm font-semibold text-ink-900">
                    🎯 Objectifs à synchroniser avec fiche de poste <span className="ml-1 text-emerald-700">({t.objectivesUpdates})</span>
                  </div>
                </div>
                <ul className="divide-y divide-ink-100 max-h-40 overflow-y-auto">
                  {objectivesUpdates.slice(0, 30).map((o) => (
                    <li key={o.evaluationId} className="px-4 py-2 text-xs text-ink-700">
                      <span className="font-medium">{o.employeeName}</span>
                      {o.addedObjectives.length > 0 && <span className="ml-2 text-emerald-700">+ {o.addedObjectives.length} nouv.</span>}
                      {o.removedObjectives.length > 0 && <span className="ml-2 text-amber-700">⚠ {o.removedObjectives.length} dépréc.</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="resync-cancel-btn">
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || nothingToDo}
            data-testid="resync-confirm-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Application…</>
            ) : (
              <>Confirmer et resynchroniser ({totalChanges})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
